import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getSelectedCustomerId } from '../selectors/customers'
import prepareHeaders from '../utils/prepareHeaders'
import { usePollTaskStatus } from './hub'

const baseUrl = '/secure/policies/purview/retention-compliance-policy'

const PENDING_DELETION = 'PendingDeletion'

/**
 * Retention policies pending removal should not appear in the live list.
 * Microsoft may surface this via Mode and/or DistributionStatus.
 */
const isPendingDeletion = (policy) =>
  policy?.Mode === PENDING_DELETION || policy?.DistributionStatus === PENDING_DELETION

export const purviewRetentionApi = createApi({
  reducerPath: 'purviewRetention',
  tagTypes: ['RetentionPolicy', 'RetentionPolicies', 'RetentionPoliciesCount', 'DeferredRuleFailures'],
  baseQuery: fetchBaseQuery({
    prepareHeaders,
    baseUrl: process.env.REACT_APP_AUGMENTT_API_URL,
  }),
  endpoints: (builder) => ({
    getRetentionPolicies: builder.query({
      query: (customerId) => ({
        url: baseUrl,
        params: { customerId },
      }),
      transformResponse: (response) => (response ?? []).filter((policy) => !isPendingDeletion(policy)),
      providesTags: (result, error, customerId) => [{ type: 'RetentionPolicies', id: customerId }],
    }),
    getRetentionPoliciesCount: builder.query({
      query: (customerId) => ({
        url: baseUrl,
        params: { customerId, countOnly: true },
      }),
      transformResponse: (response) => response?.count ?? 0,
      providesTags: (result, error, customerId) => [{ type: 'RetentionPoliciesCount', id: customerId }],
    }),
    getRetentionPolicyById: builder.query({
      query: ({ customerId, policyId }) => ({
        url: `${baseUrl}/${policyId}`,
        params: { customerId },
      }),
      transformResponse: (response) => {
        const { rawDataFlat, ...rest } = response ?? {}
        return { ...rawDataFlat, ...rest }
      },
      providesTags: (result, error, { policyId }) => [{ type: 'RetentionPolicy', id: policyId }],
    }),
    getDeferredRuleFailures: builder.query({
      query: (customerId) => ({
        url: `${baseUrl}/deferred-rule-failures`,
        params: { customerId },
      }),
      transformResponse: (response) => response ?? {},
      providesTags: (result, error, customerId) => [{ type: 'DeferredRuleFailures', id: customerId }],
    }),
    createRetentionPolicy: builder.mutation({
      query: ({ customerId, hubTaskSource, payload }) => ({
        url: baseUrl,
        body: payload,
        params: { customerId, source: hubTaskSource },
        method: 'POST',
      }),
      invalidatesTags: (result, error, { customerId }) => [
        { type: 'RetentionPolicies', id: customerId },
        { type: 'RetentionPoliciesCount', id: customerId },
      ],
    }),
    updateRetentionPolicy: builder.mutation({
      query: ({ customerId, policyId, hubTaskSource, payload }) => ({
        url: `${baseUrl}/${policyId}`,
        body: payload,
        params: { customerId, source: hubTaskSource },
        method: 'PUT',
      }),
      invalidatesTags: (result, error, { customerId, policyId }) => [
        { type: 'RetentionPolicies', id: customerId },
        { type: 'RetentionPolicy', id: policyId },
      ],
    }),
    deleteRetentionPolicy: builder.mutation({
      query: ({ customerId, policyId }) => ({
        url: `${baseUrl}/${policyId}`,
        method: 'DELETE',
        params: { customerId },
      }),
      invalidatesTags: (result, error, { customerId }) => [
        { type: 'RetentionPolicies', id: customerId },
        { type: 'RetentionPoliciesCount', id: customerId },
      ],
    }),
    retryDeferredRule: builder.mutation({
      query: ({ customerId, policyId }) => ({
        url: `${baseUrl}/${policyId}/retry-deferred-rule`,
        method: 'POST',
        params: { customerId },
      }),
    }),
  }),
})

export const {
  useGetRetentionPoliciesQuery,
  useLazyGetRetentionPoliciesQuery,
  useGetRetentionPoliciesCountQuery,
  useLazyGetRetentionPoliciesCountQuery,
  useGetRetentionPolicyByIdQuery,
  useLazyGetRetentionPolicyByIdQuery,
  useGetDeferredRuleFailuresQuery,
  useCreateRetentionPolicyMutation,
  useUpdateRetentionPolicyMutation,
  useDeleteRetentionPolicyMutation,
  useRetryDeferredRuleMutation,
} = purviewRetentionApi

export const useGetRetentionPolicies = (options = {}) => {
  const customerId = useSelector(getSelectedCustomerId)

  return useGetRetentionPoliciesQuery(customerId, { ...options, skip: !customerId || options.skip })
}

export const useGetRetentionPolicyById = ({ policyId }, options = {}) => {
  const customerId = useSelector(getSelectedCustomerId)

  return useGetRetentionPolicyByIdQuery(
    { customerId, policyId },
    { ...options, skip: !customerId || !policyId || options.skip },
  )
}

export const useGetDeferredRuleFailures = (options = {}) => {
  const customerId = useSelector(getSelectedCustomerId)

  return useGetDeferredRuleFailuresQuery(customerId, {
    ...options,
    skip: !customerId || options.skip,
    pollingInterval: options.pollingInterval ?? 60_000,
  })
}

/**
 * Re-queues a failed deferred retention-rule update, polls the HubTask, then refreshes failures/policies.
 */
export const useRetryDeferredRule = () => {
  const dispatch = useDispatch()
  const customerId = useSelector(getSelectedCustomerId)
  const [retryDeferredRuleMutation, mutationState] = useRetryDeferredRuleMutation()
  const { pollTaskStatus, isFetching: isPolling, ...pollState } = usePollTaskStatus()

  const retryDeferredRule = useCallback(
    async (policyId) => {
      if (!customerId || !policyId) {
        return { isSuccess: false, isError: true }
      }

      const enqueueResult = await retryDeferredRuleMutation({ customerId, policyId })
      const taskId = enqueueResult?.data?.taskId
      if (!taskId) {
        return { ...enqueueResult, isSuccess: false, isError: true }
      }

      // Clear the status-cell error as soon as a newer deferred task exists (PENDING).
      dispatch(purviewRetentionApi.util.invalidateTags([{ type: 'DeferredRuleFailures', id: customerId }]))

      const pollResult = await pollTaskStatus(taskId)
      if (pollResult.isSuccess) {
        dispatch(
          purviewRetentionApi.util.invalidateTags([
            { type: 'DeferredRuleFailures', id: customerId },
            { type: 'RetentionPolicies', id: customerId },
            { type: 'RetentionPolicy', id: policyId },
          ]),
        )
      } else {
        // Retry failed — surface the latest FAILED deferred task again.
        dispatch(purviewRetentionApi.util.invalidateTags([{ type: 'DeferredRuleFailures', id: customerId }]))
      }

      return pollResult
    },
    [customerId, dispatch, pollTaskStatus, retryDeferredRuleMutation],
  )

  return {
    retryDeferredRule,
    isLoading: mutationState.isLoading || isPolling,
    ...pollState,
    isFetching: mutationState.isLoading || isPolling,
  }
}
